import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Clock } from "lucide-react";

export default function Contact() {
  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white border-b border-slate-200">
        <div className="absolute inset-0 bg-dots opacity-70 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-20 md:py-28 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#22A34A] mb-4">
            Contact
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            We&apos;d love to hear from you.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            The quickest way to see QC4Health is to book a 60-minute demo.
            Prefer to send us a message? Reach out via the details below and
            our team will get back to you.
          </p>
          <Link
            to="/book-demo"
            data-testid="contact-hero-book-demo-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-8 py-3.5 font-medium transition-all hover:-translate-y-0.5 shadow-[0_10px_28px_-8px_rgba(34,163,74,0.55)]"
          >
            Book a Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* CONTACT DETAILS */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div
              className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              data-testid="contact-card-email"
            >
              <div className="w-12 h-12 rounded-xl bg-[#22A34A]/10 flex items-center justify-center mb-5">
                <Mail className="w-5 h-5 text-[#22A34A]" />
              </div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">
                Email
              </div>
              <a
                href="mailto:info@tmrglobal.com"
                data-testid="contact-general-email"
                className="text-lg font-heading font-medium text-slate-900 hover:text-[#22A34A] transition-colors"
              >
                info@tmrglobal.com
              </a>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                For product enquiries, partnerships and general questions.
              </p>
            </div>

            <div
              className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              data-testid="contact-card-region"
            >
              <div className="w-12 h-12 rounded-xl bg-[#22A34A]/10 flex items-center justify-center mb-5">
                <MapPin className="w-5 h-5 text-[#22A34A]" />
              </div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">
                Region
              </div>
              <div className="text-lg font-heading font-medium text-slate-900">
                Asia Pacific
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Supporting healthcare organisations across Australia, Hong Kong
                and the wider region.
              </p>
            </div>

            <div
              className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
              data-testid="contact-card-response"
            >
              <div className="w-12 h-12 rounded-xl bg-[#22A34A]/10 flex items-center justify-center mb-5">
                <Clock className="w-5 h-5 text-[#22A34A]" />
              </div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">
                Response Time
              </div>
              <div className="text-lg font-heading font-medium text-slate-900">
                Within 1 business day
              </div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Or book a demo directly to skip the queue and meet the team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="pb-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-black text-white p-10 md:p-14">
            <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#22A34A]/25 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#3FB8E5]/20 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#3FB8E5] mb-4">
                  The fastest way to see QC4Health
                </div>
                <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight">
                  Book a 60-minute walkthrough.
                </h2>
                <p className="mt-4 text-white/70 max-w-lg leading-relaxed">
                  Pick a time that suits you and we&apos;ll tailor the demo to
                  your governance program.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/book-demo"
                  data-testid="contact-cta-book-demo-btn"
                  className="inline-flex items-center gap-2 bg-[#22A34A] hover:bg-[#1e9042] text-white rounded-full px-8 py-4 font-medium transition-all hover:-translate-y-0.5 shadow-[0_10px_28px_-8px_rgba(34,163,74,0.55)]"
                >
                  Book a Demo
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
